import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Industries', path: '/industries' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled 
          ? 'bg-navy-900/95 backdrop-blur-md shadow-lg' 
          : 'bg-navy-900'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <div className="flex flex-col">
              <span className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-none">
                SUMMIT
              </span>
              <span className="font-body text-xs sm:text-sm font-semibold text-orange-500 tracking-widest">
                TRANSPORT & LOGISTICS
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`nav-link font-body text-sm font-semibold uppercase tracking-wide transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-orange-500'
                    : 'text-white hover:text-orange-500'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-6">
            <a
              href="tel:+18888984591"
              className="flex items-center gap-2 text-white hover:text-orange-500 transition-colors duration-200"
              data-testid="phone-link"
            >
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-orange-500" />
              </div>
              <span className="font-body text-sm font-semibold">(888) 898-4591</span>
            </a>
            <Link to="/quote" data-testid="get-quote-btn">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-body font-bold text-sm uppercase tracking-wide px-6 py-3 rounded-sm shadow-lg hover:shadow-orange-500/30 transition-shadow duration-300">
                Get Freight Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden absolute top-20 left-0 right-0 bg-navy-900 border-t border-navy-700 transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block font-body text-base font-semibold uppercase tracking-wide py-2 ${
                isActive(link.path)
                  ? 'text-orange-500'
                  : 'text-white hover:text-orange-500'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <div className="pt-4 border-t border-navy-700">
            <a
              href="tel:+18888984591"
              className="flex items-center gap-2 text-white py-2"
            >
              <Phone className="w-4 h-4 text-orange-500" />
              <span className="font-body font-semibold">(888) 898-4591</span>
            </a>
            <Link to="/quote" onClick={() => setIsMobileMenuOpen(false)} className="block mt-4">
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-body font-bold uppercase tracking-wide">
                Get Freight Quote
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};
